'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import { allowModelViewerSource, installModelViewerUrlPolicy } from '@/lib/model-viewer-url-policy';

interface ModelViewerProps {
  src: string;
  alt?: string;
  className?: string;
  autoRotate?: boolean;
  cameraControls?: boolean;
  ar?: boolean;
  style?: React.CSSProperties;
  onReady?: () => void;
}

function ModelViewerLoading() {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);

  return (
    <div
      className="flex items-center justify-center bg-gray-100"
      style={{ minHeight: '200px', minWidth: '200px' }}
    >
      <div className="text-sm text-gray-500">{ui.loading3dViewer}</div>
    </div>
  );
}

function ModelViewerComponent({
  src,
  alt = '3D Model',
  className = '',
  autoRotate = false,
  cameraControls = true,
  ar = true,
  style,
  onReady,
}: ModelViewerProps) {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);
  const modelViewerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  console.log('ModelViewer: Component rendered with props:', {
    src,
    className,
    autoRotate,
    cameraControls,
    ar,
  });

  useEffect(() => {
    // Only load in browser environment and after component is mounted
    if (typeof window === 'undefined' || !isMounted) return;

    // Load <model-viewer> only in the browser with proper async handling
    console.log(
      'ModelViewer: Attempting to load @google/model-viewer library...'
    );

    const loadModelViewer = async () => {
      try {
        // Use dynamic import with proper async handling for Next.js 15.5.2
        // this is the only place where we can use dynamic imports are ok
        // never ever use dynamic imports in convex
        await import('@google/model-viewer');

        // Wait for custom element to be defined
        if ('customElements' in window) {
          await customElements.whenDefined('model-viewer');
        }
        // Every model load may only reach the model file itself (and data:/
        // blob:), never a host named inside the glTF. Fail closed without it.
        if (!installModelViewerUrlPolicy(customElements.get('model-viewer'))) {
          setError(ui.failedToLoad3dViewerLibrary);
          return;
        }

        console.log('ModelViewer: @google/model-viewer loaded successfully');
        setIsLoaded(true);
      } catch (error) {
        console.error(
          'ModelViewer: Failed to load @google/model-viewer:',
          error
        );
        setError(ui.failedToLoad3dViewerLibrary);
      }
    };

    void loadModelViewer();
  }, [isMounted]);

  useEffect(() => {
    if (isLoaded && modelViewerRef.current && src) {
      console.log('ModelViewer: Setting up model viewer with src:', src);

      // Clear any existing content
      modelViewerRef.current.innerHTML = '';
      // This model file (and nothing it names) may load while it is shown.
      const releaseSource = allowModelViewerSource(src);

      // Create the model-viewer element
      const modelViewer = document.createElement('model-viewer');

      // Set all attributes
      modelViewer.setAttribute('src', src);
      modelViewer.setAttribute('alt', alt);
      // `ar` is a boolean attribute: its presence enables AR, whatever its
      // value. Phones without WebXR fall back to Scene Viewer (Android) or
      // Quick Look (iOS, with a USDZ generated from the GLB on the fly).
      modelViewer.toggleAttribute('ar', ar);
      if (ar) {
        modelViewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      }
      modelViewer.setAttribute('auto-rotate', autoRotate.toString());
      modelViewer.setAttribute('camera-controls', cameraControls.toString());
      modelViewer.setAttribute('loading', 'eager');
      modelViewer.setAttribute('reveal', 'auto');
      modelViewer.setAttribute('environment-image', 'neutral'); // Add neutral lighting
      modelViewer.setAttribute('shadow-intensity', '1'); // Add shadow
      // Add interaction-policy for better control
      modelViewer.setAttribute('interaction-policy', 'allow-when-focused');
      modelViewer.style.width = '100%';
      modelViewer.style.height = '100%';
      modelViewer.style.minHeight = '200px';
      modelViewer.style.display = 'block';
      modelViewer.style.backgroundColor = 'transparent'; // Transparent background

      // Add more debugging
      console.log('ModelViewer: Created element with attributes:', {
        src: modelViewer.getAttribute('src'),
        ar: modelViewer.getAttribute('ar'),
        autoRotate: modelViewer.getAttribute('auto-rotate'),
        cameraControls: modelViewer.getAttribute('camera-controls'),
      });

      // Track loading progress
      let progressReported = false;
      let fileSizeBytes = 0;
      const headerController = new AbortController();

      // Check file size via HEAD request (for timeout calculation)
      fetch(src, { method: 'HEAD', signal: headerController.signal })
        .then((response) => {
          console.log('ModelViewer: File headers:', {
            contentType: response.headers.get('content-type'),
            contentLength: response.headers.get('content-length'),
            status: response.status,
            url: response.url,
          });

          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            fileSizeBytes = parseInt(contentLength, 10);
            console.log(
              `ModelViewer: File size: ${Math.round((fileSizeBytes / 1024 / 1024) * 100) / 100}MB`
            );
          }

          const contentType = response.headers.get('content-type');
          if (contentType === 'application/octet-stream') {
            console.warn(
              'ModelViewer: File served with generic MIME type. This might cause loading issues.'
            );
          }
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          console.warn('ModelViewer: Could not fetch file headers:', error);
        });

      // Use a longer timeout for large 3D models (90 seconds default, which should handle 20MB+ files)
      // The HEAD request is async, so we use a conservative default timeout
      const timeoutMs = 90000; // 90 seconds - sufficient for large GLB files

      // Add a timeout to detect if model never loads
      const loadTimeout = setTimeout(() => {
        if (!progressReported) {
          console.warn(
            `ModelViewer: Model loading timeout after ${timeoutMs / 1000} seconds`
          );
          console.warn('ModelViewer: Current element attributes:', {
            src: modelViewer.getAttribute('src'),
          });
          console.warn(
            'ModelViewer: No progress events received - file may be too large or URL inaccessible'
          );
          setError(ui.failedToLoad3dModel);
        } else {
          console.warn(
            `ModelViewer: Model loading timeout after ${timeoutMs / 1000} seconds, but progress was reported`
          );
          setError(ui.failedToLoad3dModel);
        }
      }, timeoutMs);

      // Add comprehensive event listeners
      const handleLoad = () => {
        console.log('ModelViewer: Model loaded successfully');
        clearTimeout(loadTimeout);
        progressReported = true;
        setError(null);
        onReady?.();
      };

      const handleError = (event: Event) => {
        console.warn('ModelViewer: Error loading model:', event);
        setError(ui.failedToLoad3dModel);
      };

      const handleModelVisibility = (event: Event) => {
        console.log('ModelViewer: Model visibility changed:', event);
      };

      const handleProgress = (event: Event) => {
        progressReported = true;
        // The progress event from model-viewer can be either ProgressEvent or CustomEvent
        const progressEvent = event as unknown as CustomEvent<{
          totalProgress: number;
        }>;
        const progress = progressEvent.detail?.totalProgress ?? 0;
        console.log(
          'ModelViewer: Loading progress:',
          `${Math.round(progress * 100)}%`
        );
      };

      const handleBeforeRender = () => {
        console.log('ModelViewer: Before render event');
      };

      const handleCameraChange = () => {
        console.log('ModelViewer: Camera change event');
      };

      modelViewer.addEventListener('load', handleLoad);
      modelViewer.addEventListener('error', handleError);
      modelViewer.addEventListener('model-visibility', handleModelVisibility);
      modelViewer.addEventListener('progress', handleProgress);
      modelViewer.addEventListener('before-render', handleBeforeRender);
      modelViewer.addEventListener('camera-change', handleCameraChange);

      // Log when element is added to DOM
      console.log('ModelViewer: Appending model-viewer element to DOM');
      modelViewerRef.current.appendChild(modelViewer);

      // Check if element was properly added
      const verifyTimer = setTimeout(() => {
        const el = modelViewerRef.current?.querySelector('model-viewer');
        const elementSrc =
          el instanceof Element ? el.getAttribute('src') : null;
        console.log('ModelViewer: Element in DOM check:', {
          elementExists: !!el,
          elementSrc,
        });
      }, 1000);

      return () => {
        headerController.abort();
        clearTimeout(loadTimeout);
        clearTimeout(verifyTimer);
        modelViewer.removeEventListener('load', handleLoad);
        modelViewer.removeEventListener('error', handleError);
        modelViewer.removeEventListener(
          'model-visibility',
          handleModelVisibility
        );
        modelViewer.removeEventListener('progress', handleProgress);
        modelViewer.removeEventListener('before-render', handleBeforeRender);
        modelViewer.removeEventListener('camera-change', handleCameraChange);
        modelViewer.remove();
        releaseSource();
      };
    }

    return undefined;
  }, [isLoaded, src, alt, ar, autoRotate, cameraControls, onReady]);

  if (error) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-red-100`}
        style={style}
      >
        <div className="text-sm text-red-500">{error}</div>
      </div>
    );
  }

  // Don't render anything on server-side to prevent hydration mismatch
  if (!isMounted) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-gray-100`}
        style={style}
      >
        <div className="text-sm text-gray-500">{ui.loading}</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-gray-100`}
        style={style}
      >
        <div className="text-sm text-gray-500">{ui.loading3dViewer}</div>
      </div>
    );
  }

  if (!src) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-red-100`}
        style={style}
      >
        <div className="text-sm text-red-500">{ui.noSrcProvided}</div>
      </div>
    );
  }

  return (
    <div
      ref={modelViewerRef}
      className={className}
      style={{
        minHeight: '200px', // Ensure minimum height
        minWidth: '200px', // Ensure minimum width
        display: 'block', // Ensure it's displayed
        position: 'relative', // Ensure proper positioning
        backgroundColor: 'transparent', // Transparent background
        overflow: 'hidden', // Prevent overflow
        ...style,
      }}
    />
  );
}

// Export as dynamic component to prevent SSR issues
export const ModelViewer = dynamic(
  () => Promise.resolve(ModelViewerComponent),
  {
    ssr: false,
    loading: () => <ModelViewerLoading />,
  }
);
